// Local (not UTC) ISO-date helpers for the frontend.
//
// Phase 208 / card 208-E2. Mirrors `tests/helpers/local-date.js` (the
// Node-side helper for server tests) so frontend tests build their
// fixtures on the SAME calendar the server reads.
//
// WHY THIS EXISTS
// ---------------
// A date-only string handed to the Date constructor is parsed as **UTC
// midnight** by the ECMAScript spec, not as local midnight. In any timezone
// with a negative UTC offset (the Americas) that instant is the PREVIOUS
// local day:
//
//     new Date(<a 'YYYY-MM-DD' string>).getDate()
//       → 28  under TZ=UTC / Asia/Hong_Kong / Pacific/Kiritimati
//       → 27  under TZ=America/New_York   (UTC-4)
//
// The server computes recurring-reminder windows from the **local**
// calendar (`today.getDate()`), so a test that simulates "the 28th" with a
// date-only string actually simulates the 27th in New York — and a
// `day_of_month: 28` rule is then skipped. That failure is invisible
// under a UTC-only runner.
//
// These helpers deliberately build dates from local calendar fields and
// NEVER convert a Date to its UTC ISO string (that is the trap).

/** Local calendar date of `d` as YYYY-MM-DD. */
export function localIso(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Local calendar date `n` days from `base` (default: now), as YYYY-MM-DD.
 *
 * Constructed via `new Date(y, m, d + n)` rather than
 * `setDate(getDate() + n)` followed by a UTC ISO conversion: the former
 * stays on the local calendar (no UTC shift) and the local constructor
 * normalises month/year rollover, including across DST boundaries.
 */
export function localIsoOffsetDays(n, base = new Date()) {
  return localIso(new Date(base.getFullYear(), base.getMonth(), base.getDate() + n));
}

/** Local calendar date today, as YYYY-MM-DD. */
export function localToday() {
  return localIso(new Date());
}

/** Local YYYY-MM for `d` (default: now). */
export function localYm(d = new Date()) {
  return localIso(d).slice(0, 7);
}

/**
 * A Date for a LOCAL calendar date, given as 'YYYY-MM-DD' (or 'YYYY-MM').
 *
 * @param {string} s  'YYYY-MM-DD' or 'YYYY-MM'
 */
export function localDate(s) {
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(String(s).trim());
  if (!m) throw new Error(`localDate: expected YYYY-MM-DD, got ${JSON.stringify(s)}`);
  const y = Number(m[1]);
  const mon = Number(m[2]) - 1;
  const day = m[3] ? Number(m[3]) : 1;

  // Reject out-of-range calendar values BEFORE constructing the Date.
  // The local Date constructor normalises silently: new Date(2026, 1, 30)
  // is 2026-03-02 and new Date(2026, 12, 1) is 2027-01-01. A typo'd
  // fixture date would therefore SHIFT a due date instead of failing
  // loudly — and a silently shifted date is exactly the class of bug this
  // helper exists to prevent (see the Phase 207 UTC trap in the header).
  if (mon < 0 || mon > 11) {
    throw new Error(`localDate: month out of range in ${JSON.stringify(s)} (expected 01-12)`);
  }
  if (day < 1) {
    throw new Error(`localDate: day out of range in ${JSON.stringify(s)} (expected 01-31)`);
  }
  const daysInMonth = new Date(y, mon + 1, 0).getDate();
  if (day > daysInMonth) {
    throw new Error(
      `localDate: ${JSON.stringify(s)} has no such day (${y}-${String(mon + 1).padStart(2, '0')} has ${daysInMonth} days)`
    );
  }

  // The local Date constructor handles DST boundaries correctly.
  return new Date(y, mon, day);
}

/** Normalise a string-or-Date argument to local midnight. */
function toLocalMidnight(value) {
  const d = value instanceof Date ? value : localDate(value);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Whole calendar days from `a` to `b` — i.e. `b - a` in LOCAL calendar days.
 *
 * Both arguments are LOCAL calendar dates, given as 'YYYY-MM-DD' (or
 * 'YYYY-MM'), or as a Date. The result is a signed integer: negative when
 * `b` is before `a`.
 *
 * The difference is computed on the local calendar, not on elapsed
 * milliseconds, so it is unaffected by DST transitions — a day is a day
 * even when it is 23 or 25 hours long.
 *
 * @param {string|Date} a
 * @param {string|Date} b
 */
export function localDayDiff(a, b) {
  const da = toLocalMidnight(a);
  const db = toLocalMidnight(b);
  // Round, because a DST-shifted midnight is not exactly 24h apart.
  return Math.round((db - da) / 86400000);
}

/** Local Date for `s` (YYYY-MM-DD), at noon — avoids DST edge cases. */
export function localDateNoon(s) {
  const d = localDate(s);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0);
}
